// Types for GET /check-ins/user/{user}/filter

import { LaravelPaginatedResponse } from "./user";
import { badge } from "../utils/badgeColors";

export type AdjustmentDecision =
  | "HOLD_COMPLIANCE"
  | "HOLD_WITHIN_TARGET"
  | "MILD_ADJUSTMENT"
  | "SEVERE_ADJUSTMENT";

export type LifestyleZone = "GREEN" | "YELLOW" | "RED";

// Rendered with formatLabels() - no label map needed.
export type AdjustmentReason =
  | "hold_compliance"
  | "insufficient_history"
  | "held_within_target_fat_loss"
  | "mild_undershoot_fat_loss"
  | "severe_undershoot_fat_loss"
  | "mild_overshoot_fat_loss"
  | "severe_overshoot_fat_loss"
  | "held_within_target_muscle_gain"
  | "mild_undershoot_muscle_gain"
  | "severe_undershoot_muscle_gain"
  | "mild_overshoot_muscle_gain"
  | "severe_overshoot_muscle_gain"
  | "held_within_band_maintenance"
  | "gentle_drift_correction_maintenance"
  | "strong_drift_correction_maintenance"
  | "applied_planned_increment_improve_metabolism"
  | "held_overshoot_improve_metabolism"
  | "reverted_severe_overshoot_improve_metabolism"
  | "accelerated_below_target_improve_metabolism";

export interface DailyConsumptionEntry {
  kcal: number;
  protein_g: number;
  carb_g: number;
  fat_g: number;
  fiber_g: number | null; // null on legacy rows
  sodium_mg: number | null;
  sat_fat_g: number | null;
  log_count: number;
}

// Keyed by YYYY-MM-DD
export type DailyConsumptionSnapshot = Record<string, DailyConsumptionEntry>;

export interface UserCheckIn {
  id: number;
  user_id: number;
  closed_nutrition_cycle_id: number;
  generated_nutrition_cycle_id: number | null;

  // decimal:2 casts - serialized as strings
  average_weight_kg: string;
  rolling_average_weight_kg: string | null;
  weight_change_pct: string | null;

  cycle_actual_kcal: number;
  cycle_actual_protein_g: string;
  cycle_actual_carb_g: string;
  cycle_actual_fat_g: string;

  daily_consumption_snapshot: DailyConsumptionSnapshot | null;

  is_calorie_compliant: boolean;
  is_protein_compliant: boolean;
  is_sodium_compliant: boolean;
  is_fiber_compliant: boolean;
  is_activity_consistent: boolean;
  is_menstrual_stable: boolean | null;

  adjustment_decision: AdjustmentDecision;
  adjustment_reason: AdjustmentReason;
  applied_kcal_delta: number;
  safety_cap_applied: boolean;
  previous_target_kcal: number;
  new_target_kcal: number;
  computed_maintenance_kcal_snapshot: number | null;

  lifestyle_score_sleep: number | null;
  lifestyle_score_appetite: number | null;
  lifestyle_score_food_noise: number | null;
  lifestyle_score_mood: number | null;
  lifestyle_score_energy: number | null;
  lifestyle_score_muscle_soreness: number | null;
  lifestyle_zone: LifestyleZone | null;

  submitted_at: string;
  created_at: string;
  updated_at: string;
}

export interface CheckInFilters {
  submitted_at?: string; // YYYY-MM-DD, exact day match
}

export interface UserCheckInsApiResponse {
  data: LaravelPaginatedResponse<UserCheckIn>;
}

export const ADJUSTMENT_DECISION_LABELS: Record<AdjustmentDecision, string> = {
  HOLD_COMPLIANCE: "Hold (Compliance)",
  HOLD_WITHIN_TARGET: "Hold (On Target)",
  MILD_ADJUSTMENT: "Mild Adjustment",
  SEVERE_ADJUSTMENT: "Severe Adjustment",
};

export const ADJUSTMENT_DECISION_BADGE_CLASSES: Record<
  AdjustmentDecision,
  string
> = {
  HOLD_WITHIN_TARGET: badge("green"),
  HOLD_COMPLIANCE:
    "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  MILD_ADJUSTMENT: badge("yellow"),
  SEVERE_ADJUSTMENT: badge("red"),
};

export const LIFESTYLE_ZONE_BADGE_CLASSES: Record<LifestyleZone, string> = {
  GREEN: badge("green"),
  YELLOW: badge("yellow"),
  RED: badge("red"),
};
